import type { components } from '@/types/api/v1'
import dayjs, { type Dayjs } from 'dayjs'
import tz from 'dayjs/plugin/timezone'
import RBush, { type BBox } from 'rbush'
import { useMemo } from 'react'
import type { ConflictInfo } from '../../utils/ConflictValidators'
import { ItemDatabase } from '../../utils/ItemDatabase'
import { conflictValidators } from './validators'
dayjs.extend(tz)

type DateRange = {
  start: Dayjs
  end: Dayjs
}

const toBoundingBox = <T extends DateRange>(item: T) => ({
  minX: item.start.valueOf(),
  maxX: item.end.valueOf() - 1, //so that ranges are exclusive
  minY: 0,
  maxY: 0,
})

class DateRangeTree<T extends DateRange> extends RBush<T> {
  toBBox(item: T): BBox {
    return toBoundingBox(item)
  }
  compareMinX(a: T, b: T): number {
    return a.start.valueOf() - b.start.valueOf()
  }
  //since our coordinates are only single-axis (startDate, endDate), we don't need to compare a second axis
  compareMinY(_a: T, _b: T): number {
    return 0
  }
}

type ScheduleItem = components['schemas']['DriverScheduleDay']
export type MappedScheduleItem = ScheduleItem & DateRange
export type ScheduleItemWithConflicts = MappedScheduleItem & ConflictInfo<MappedScheduleItem>
export type ScheduleOverlapArgs = DateRange & { event_id?: string | null }

/**
 * this tractor/trailer is assigned to [drivers] : overlap where tractor/trailer id === && there is driver id
 * [driver/carrier].name already has a shift at this time: overlap where driver id ===
 * this tractor/trailer is OOS: overlap where tractor/trailer id ===, && event type === oos event
 * this tractor/trailer is inactive: overlap where tractor/trailer id === && event type === inactive
 */

export class ScheduleItemStore {
  tree = new DateRangeTree<MappedScheduleItem>(5)
  constructor(items: Array<MappedScheduleItem>) {
    this.tree.load(items)
  }
  getOverlappingSchedules = (args: ScheduleOverlapArgs) =>
    this.tree.search(toBoundingBox(args)).filter((x) => !args.event_id || x.event_id !== args.event_id)
  /**
   * NOTE: a 'conflict' is simply a date overlap where some kind of validation rules fail. each rule that fails will
   * add another 'conflict'. SINCE there is not currently a view for 'schedules without a driver' (the tractor view
   * only shows items for scheduled drivers), we will only represent conflicts that have a driver assigned.
   */
  getConflicts = (item: MappedScheduleItem) =>
    conflictValidators.getInvalidItems(item, this.getOverlappingSchedules(item))
}

//helper to get the index on 'start day'
export const getDayKey = (date: Dayjs) => date.format('MM-DD')

//create indexes for faster lookups
export const createScheduleItemDatabase = (items: ScheduleItemWithConflicts[]) =>
  new ItemDatabase(items, (x) => x.event_id, {
    driver: (x) => x.driver_or_carrier_id ?? '',
    day: (x) => getDayKey(x.start),
    tractor: (x) => x.tractor_id ?? '',
  })

export type ScheduleItemDatabaseKey = keyof ReturnType<typeof createScheduleItemDatabase>['indexMap']

export const useScheduleDataStore = (items: Array<ScheduleItem>) =>
  useMemo(() => {
    const mappedItems = items.map((x) => ({
      ...x,
      start: dayjs(x.shift_start_utc).tz(x.timezone_data?.depot as string),
      end: dayjs(x.shift_end_utc).tz(x.timezone_data?.depot as string),
      driver_name: x.driver_name ?? 'Unknown driver',
    }))
    const tree = new ScheduleItemStore(mappedItems)
    const itemsWithConflicts = mappedItems.map((x) => ({
      ...x,
      ...tree.getConflicts(x),
    }))
    return {
      data: itemsWithConflicts,
      tree,
    }
  }, [items])
